import { useEffect, useState } from "react";
import { useDispatch } from "react-redux";

import { setSelectedShapefile } from "store/features";

import { TilesStatus } from "api/shapefile";

import { ShapefileFileType } from "models/projects";

import { Icon_Draggable } from "components/icons";
import {
  useProjectContext,
  useProjectDispatch
} from "components/project/projects/context";

import { useUser } from "../../../../../../../../../hooks";
import { ShapefileStatusBadge } from "../../../../../../../shapefiles/components/ShapefileStatusBadge";
import { useShapefilesTilesStatusQuery } from "../../../../../../../shapefiles/queries/useShapefilesTilesStatusQuery";
import {
  FlexItem,
  LayerActionsWrapper,
  LeafTitle,
  ProjectShapefileActions,
  ProjectShapefileColorPicker,
  ShapefileLayerSettings
} from "../../../../shared";
import { useRightClickContextMenu } from "../../../../shared/hooks/useRightClickContextMenu";
import { useStructuredNodeContext } from "../../context/StructuredNodeContext";
import { setInEditMode } from "../../context/reducer/structuredNodeReducer";

export interface IShapefileNode {
  node;
}

export function ShapefileNode({ node }: IShapefileNode) {
  const projectDispatch = useProjectDispatch();
  const { selectedProjectId } = useProjectContext();

  const [{ originalTitle }, nodeDispatch] = useStructuredNodeContext();
  const dispatch = useDispatch();
  const { hasTrialAccount, isReadonly } = useUser();

  const canExportShapefile =
    node?.fileType !== ShapefileFileType.GeoTiff && !hasTrialAccount && !isReadonly;

  const { openContextMenu, contextMenuObject, nodeRef } = useRightClickContextMenu();

  const [localStatus, setLocalStatus] = useState(node.tilesStatus);

  // Sync localStatus with node.tilesStatus when it changes
  useEffect(() => {
    setLocalStatus(node.tilesStatus);
  }, [node.tilesStatus]);
  const shouldPoll =
    localStatus === TilesStatus.Pending ||
    localStatus === TilesStatus.Processing ||
    localStatus === TilesStatus.ProcessingHighZoom ||
    localStatus === TilesStatus.LowZoomReady;

  const { data: statusData } = useShapefilesTilesStatusQuery({
    shapefileIds: node.shapefileId ? [node.shapefileId] : [],
    enabled: shouldPoll,
    projectId: selectedProjectId
  });

  useEffect(() => {
    if (statusData?.[0]?.tilesStatus) {
      setLocalStatus(statusData[0].tilesStatus);
    }
  }, [statusData]);

  // Update shapefile-processing class on tree node when status changes.
  // No dependency array — must re-apply after drag-and-drop which recreates the DOM node.
  useEffect(() => {
    if (!nodeRef.current) return;

    // Find the base tree node element
    const treeNode = nodeRef.current.closest(".shapefile-leaf");
    if (!treeNode) return;

    if (
      localStatus === TilesStatus.Pending ||
      localStatus === TilesStatus.Processing ||
      localStatus === TilesStatus.ProcessingHighZoom ||
      localStatus === TilesStatus.LowZoomReady
    ) {
      treeNode.classList.add("shapefile-processing");
    } else {
      treeNode.classList.remove("shapefile-processing");
    }
  });

  return (
    <LeafTitle
      ref={nodeRef}
      onContextMenu={openContextMenu}
      // `StructuredNodeManager` renders this node only for `projectShapefile`
      // layers, so this testid means specifically "a layer the org uploaded to
      // this project" — the guided walkthrough needs to tell those apart from
      // the org/EVA-Research base layers, which look identical in the DOM.
      data-testid="project-shapefile-node">
      <div className="drag-container">
        <Icon_Draggable />
      </div>
      <FlexItem>
        <ProjectShapefileColorPicker
          shapefileId={node.shapefileId}
          color={node.layer.style.color}
          strokeColor={node.layer.style.strokeColor}
          thickness={node.layer.style.thickness}
          lineStyle={
            node.fileType !== ShapefileFileType.GeoTiff
              ? node.layer.style.lineStyle ?? "solid"
              : undefined
          }
          fillPattern={
            node.fileType !== ShapefileFileType.GeoTiff
              ? node.layer.style.fillPattern ?? "none"
              : undefined
          }
          opacity={node.layer.style.opacity}
          fileType={node.fileType}
        />
        <label title={originalTitle}>{originalTitle}</label>
        {localStatus && <ShapefileStatusBadge tilesStatus={localStatus} />}
      </FlexItem>
      <FlexItem>
        <LayerActionsWrapper>
          <ShapefileLayerSettings shapefileId={node.shapefileId} />
          <ProjectShapefileActions
            canExportShapefile={canExportShapefile}
            shapefileId={node.shapefileId}
            setIsRenaming={() =>
              setInEditMode(nodeDispatch, true, (isDraggable) =>
                projectDispatch({ payload: { isTreeDraggable: isDraggable } })
              )
            }
            fileName={originalTitle || node.title}
            viewShapefileDetails={() => dispatch(setSelectedShapefile(node))}
            contextMenu={contextMenuObject}
          />
        </LayerActionsWrapper>
      </FlexItem>
    </LeafTitle>
  );
}
